import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import cls from 'classnames';

import isToday from 'date-fns/isToday';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _noop from 'lodash/noop';

import { BusinessTravelCheckbox } from '@ott/business-travel-checkbox';
import Button from '@ott/button';
import FirstErrorShower from '@ott/first-error-shower';
import { l10n } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';
import SearchHistory from '@ott/search-history';

import DatePicker from './blocks/DatePicker';
import DestinationField from './blocks/DestinationField';
import RoomsPicker from './blocks/RoomsPicker';
import TimePicker from './blocks/TimePicker';
import { WHERE_CAN_USE_CROSS_SEARCH } from './constants';
import useErrors from './hooks/useErrors';
import useHistory from './hooks/useHistory';
import usePrev from './hooks/usePrev';
import useSave from './hooks/useSave';
import getResolver from './resolver';
import {
  AnalyticsMarkersGetter,
  Destination,
  FormState,
  HotelsConfig,
  Query,
  SubmitPayload,
  RenderContext,
} from './types';
import { getMultiroomParamsByConfig } from './utilities';
import { getCurrentSearchParams } from './utilities/getCurrentSearchParams';
import getSubmitPayload from './utilities/getSubmitPayload';

import styles from './HotelsSearchForm.scss';

type Props = {
  /**
   * Должна ли быть возможность указать направление
   */
  hasDestination?: boolean;

  /**
   * Должна ли отображаться история поисков
   */
  hasHistory?: boolean;

  /**
   * Должны ли показывать дополнительные селекты раннего/позднего заезда/выезда
   * */
  hasAdditionalInfoSelects?: boolean;

  /**
   * Доп стилизация для полей серч формы
   * */
  childrenClassNames?: {
    dates?: string;
    datesTrigger?: string;
    rooms?: string;
    roomsTrigger?: string;
    form?: string;
    submit?: string;
  };

  /**
   * Вид формы. Рекомендуется использовать column для модалов
   */
  fieldsAlign?: 'row' | 'column';

  /**
   * Локатор для submit кнопки
   */
  submitGtmLocator?: string;

  /**
   * API baseurl для запросов
   */
  hapi?: string;

  /**
   * hotelsConfig - конфиг отелей.
   * Нужен для определения параметров мультирума и времени заезда/выезда.
   * Если нет, то можно сымитировать (см. описание типа).
   */
  hotelsConfig: HotelsConfig;

  /**
   * контекст рендеринга формы (проще говоря где форма отображается)
   */
  context?: RenderContext;

  /**
   * query-параметры.
   * useLocation не используется, так как в месте использования
   * компонента qhistory может не быть, поэтому получение query
   * происходит снаружи, затем передается через пропсы
   */
  query?: Query;

  /**
   * Показать форму с пустым date-пикером
   */
  noInitialDates?: boolean;

  /**
   * Коллбэк, вызываемый на submit формы
   */
  onSubmit: (payload: SubmitPayload) => void;

  /**
   * Геттер для маппинга параметров из query для аналитики
   */
  analyticsMarkersGetter?: AnalyticsMarkersGetter;

  /**
   * Можно передать параметр, чтобы установить начальный destination явно,
   * а не пытаться определить его по query/lastSearch/crossSearch/etc
   */
  initialDestination?: Destination;

  /**
   * Светлый или темный контейнер под формой (некоторые элементы будут менять цвет, чтобы повысить заметность)
   */
  backgroundTheme?: 'dark' | 'light';

  /**
   * Оставлять открытым календарь выбора дат открытым или нет при кликах вне контейнера, если не
   * выбрана дата
   * выпиливаем или оставляем после завершения теста SEO-8317
   */
  preventCloseCalendarOnOutsideClick?: boolean;

  /**
   * Определяет отображение чекбокса "Путешествую по работе" под формой поиска.
   * @see {https://onetwotripdev.atlassian.net/browse/REF-201 Требования}
   */
  showBusinessTripCheckbox?: boolean;
};

const HotelsSearchForm: React.FC<Props> = ({
  hasAdditionalInfoSelects = true,
  hasDestination = true,
  hasHistory = true,
  childrenClassNames = {},
  fieldsAlign = 'row',
  submitGtmLocator,
  hapi,
  hotelsConfig,
  context = 'ott',
  query = {},
  noInitialDates = false,
  onSubmit = _noop,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  analyticsMarkersGetter = (() => {}) as AnalyticsMarkersGetter,
  initialDestination,
  backgroundTheme = 'light',
  preventCloseCalendarOnOutsideClick = false,
  showBusinessTripCheckbox = false,
}: Props) => {
  const { isTouchDevice } = useLayoutContext();

  const [currentParams, setCurrentParams] = useState(
    getCurrentSearchParams(query, {
      hotelsConfig,
      canUseCrossSearch: WHERE_CAN_USE_CROSS_SEARCH.includes(context),
    })
  );

  const initialValues = useMemo(() => {
    const form: Partial<FormState> = {
      rooms: currentParams.rooms,
      dates: {
        startDate: currentParams.startDate,
        endDate: currentParams.endDate,
      },
      earlyCheckin: currentParams.earlyCheckin,
      lateCheckout: currentParams.lateCheckout,
      to: initialDestination || currentParams.to || null,
    };

    if (noInitialDates) {
      form.dates = {
        startDate: null,
        endDate: null,
      };
    }

    const currentDestinationName = currentParams.to?.name || '';
    const givenDestinationName = initialDestination?.name || '';

    return {
      form,
      hasInitialDestination: Boolean(initialDestination),
      requestedDestination: initialDestination ? givenDestinationName : currentDestinationName,
    };
  }, []);

  const { control, watch, handleSubmit, setValue, getValues } = useForm<FormState>({
    defaultValues: initialValues.form,
    resolver: getResolver({ skippedFields: hasDestination ? [] : ['to'] }),
  });

  const multiroomParams = getMultiroomParamsByConfig(hotelsConfig);
  const prevMultiroomParams = usePrev(multiroomParams);

  useEffect(() => {
    if (!_isEqual(multiroomParams, prevMultiroomParams)) {
      const updatedParams = getCurrentSearchParams(query, {
        hotelsConfig,
        canUseCrossSearch: WHERE_CAN_USE_CROSS_SEARCH.includes(context),
      });

      setValue('rooms', updatedParams.rooms);
    }
  });

  useEffect(() => {
    const updatedDestination = getValues('to');

    const updatedParams = getCurrentSearchParams(query, {
      hotelsConfig,
      canUseCrossSearch: WHERE_CAN_USE_CROSS_SEARCH.includes(context),
      countryCode: updatedDestination?.country_code,
    });

    setCurrentParams(updatedParams);
  }, [hotelsConfig, query, context, getValues('to')]);

  const { errors, hasErrors, formRef } = useErrors({ control });
  const [times, setTimes] = useState(0);
  const count =
    (callable: (...args: any[]) => any) =>
    (...args: any[]) => {
      setTimes((prev: number) => prev + 1);
      callable(...args);
    };

  const { saveCrossSearch, saveLastSearch } = useSave({ hasDestination });
  const {
    saveRecord: saveHistoryRecord,
    records: historyRecords,
    submitRecord: submitHistoryRecord,
  } = useHistory(onSubmit);

  const currentDestination = watch('to');
  const { startDate } = watch('dates');

  const canSpecifyTime = _get(hotelsConfig, 'features.check_dates_options', false);
  const canSpecifyCheckinTime = startDate ? !isToday(startDate) : false;

  const destinationGmt = _get(currentDestination, 'timezone_gmt');

  const hasHistoryItems = hasHistory && historyRecords.length !== 0;

  const submit = (values: FormState) => {
    const payload = getSubmitPayload(values, query, {
      analyticsMarkersGetter,
      canSpecifyTime,
      canSpecifyCheckinTime,
    });

    if (hasDestination) {
      const { dates, to } = values;
      const { name } = to as Destination;
      const { startDate: fromDate, endDate: toDate } = dates as { startDate: Date; endDate: Date };

      saveCrossSearch(values);
      saveLastSearch(values);

      if (hasHistory) {
        saveHistoryRecord({
          ...payload,
          route: {
            fromDate,
            toDate,
            to: name,
          },
        });
      }
    }

    onSubmit(payload);
  };

  const historyNode = (
    <SearchHistory
      items={historyRecords}
      project="hotels"
      handleSearch={submitHistoryRecord}
      foldSameDate={true}
    />
  );

  return (
    <div data-locator="HotelsSearchForm">
      <form
        onSubmit={count(handleSubmit(submit))}
        autoComplete="off"
        ref={formRef}
        className={cls(styles.searchForm, {
          [styles.rowForm]: fieldsAlign === 'row',
          [styles.columnForm]: fieldsAlign === 'column',
          [styles.noDestination]: !hasDestination,
          [styles.noHistory]: !hasHistory,
          [styles.noTimeControls]: !canSpecifyTime,
        })}
        data-search-form-context={context}
      >
        <FirstErrorShower
          errors={errors}
          show={hasErrors}
          time={times}
          container={formRef.current}
          effect="transition"
          withFocus={!isTouchDevice}
        />
        <div className={cls(styles.form, childrenClassNames.form)}>
          <div className={styles.main}>
            {hasDestination && (
              <div className={cls(styles.field, styles.destination)}>
                <DestinationField
                  control={control}
                  hapi={hapi}
                  initialTriggerValue={initialValues.requestedDestination}
                  initialCityId={initialValues.form.to?.id}
                  hasInitialDestination={initialValues.hasInitialDestination}
                  triggerClassName={styles.destinationTrigger}
                  context={context}
                />
              </div>
            )}
            <div className={cls(styles.field, styles.dates, childrenClassNames.dates)}>
              <DatePicker
                control={control}
                putOverlayToRoot={fieldsAlign === 'column'}
                destinationGmt={destinationGmt}
                triggerClassName={cls(styles.datesTrigger, childrenClassNames.datesTrigger)}
                context={context}
                defaultCalendarOpened={currentParams.calendarOpened}
                preventCloseCalendarOnOutsideClick={preventCloseCalendarOnOutsideClick}
                maxDays={currentParams.maxDays}
              />
            </div>
            <div className={cls(styles.field, styles.rooms, childrenClassNames.rooms)}>
              <RoomsPicker
                control={control}
                hotelsConfig={hotelsConfig}
                watch={watch}
                triggerClassName={cls(styles.roomsTrigger, childrenClassNames.roomsTrigger)}
                context={context}
              />
            </div>
          </div>
          {hasAdditionalInfoSelects && (
            <div
              className={cls(styles.advanced, {
                [styles.advancedWithHistory]: hasHistoryItems,
              })}
            >
              {hasHistoryItems ? <div className={styles.rowHistory}>{historyNode}</div> : null}
              <div className={styles.controlsWrap}>
                {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
                  <div className={styles.businessTravelCheckboxWrap}>
                    <BusinessTravelCheckbox vertical="hotels" />
                  </div>
                )}
                <div className={styles.time}>
                  {canSpecifyTime && (
                    <TimePicker
                      control={control}
                      theme={backgroundTheme === 'dark' ? 'light' : 'dark'}
                      canSpecifyCheckinTime={canSpecifyCheckinTime}
                      triggerClassName={styles.timesTrigger}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.submit}>
          <Button
            type="submit"
            withFullWidth={true}
            specificLocators="submit-form-button"
            trackingLocators={submitGtmLocator}
            className={childrenClassNames.submit}
          >
            {l10n('hotelsSearchForm.submit')}
          </Button>
          {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
            <div className={styles.businessTravelCheckboxWrapMobile}>
              <BusinessTravelCheckbox vertical="hotels" />
            </div>
          )}
        </div>
        {hasHistoryItems && <div className={styles.columnHistory}>{historyNode}</div>}
      </form>
    </div>
  );
};

export default HotelsSearchForm;
